import React from "react";
import { sparklePath } from "./expressions";

// ── Shine / sparkle item types ──
interface ShineItemPrimary {
  dx: number;
  dy: number;
  rx: number;
  ry: number;
}

interface ShineItemSecondary {
  dx: number;
  dy: number;
  r: number;
}

// ── Anime eye descriptor ──
export interface AnimeEyeDescriptor {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  irisRx: number;
  irisRy: number;
  irisOy: number;
  pupilR: number;
  pupilOy: number;
  shine: [ShineItemPrimary, ShineItemSecondary];
  lidTop: number;
  lidBot?: number;
  lashOut: boolean;
  heart?: boolean;
}

interface AnimeEyeProps {
  e: AnimeEyeDescriptor;
  color: string;
  irisColor: string;
  bgColor: string;
  scleraColor: string;
  flip: boolean;
  clipId: string;
}

const AnimeEye: React.FC<AnimeEyeProps> = ({
  e,
  color,
  irisColor,
  bgColor,
  scleraColor,
  flip,
  clipId,
}) => {
  const dir = flip ? -1 : 1;
  const lidTopY = e.cy - e.ry + e.ry * 2 * e.lidTop;
  const lidBot = e.lidBot || 0;
  const lidBotY = e.cy + e.ry - e.ry * 2 * lidBot;
  const t = "all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)";
  const tFast = "all 0.3s ease";

  const clipY = e.lidTop > 0 ? lidTopY : e.cy - e.ry - 3;
  const clipBottom = lidBot > 0 ? lidBotY : e.cy + e.ry + 3;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect
            x={e.cx - e.rx - 2}
            y={clipY}
            width={(e.rx + 2) * 2}
            height={Math.max(0, clipBottom - clipY)}
            style={{ transition: t }}
          />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {/* Eye sclera */}
        <ellipse
          cx={e.cx}
          cy={e.cy}
          rx={e.rx}
          ry={e.ry}
          fill={scleraColor}
          stroke="black"
          strokeWidth={0.6}
          style={{ transition: t }}
        />

        {/* Iris — large, dark with inner lighter ring */}
        <ellipse
          cx={e.cx}
          cy={e.cy + e.irisOy}
          rx={e.irisRx}
          ry={e.irisRy}
          fill={color}
          style={{ transition: t }}
        />

        {/* Inner iris ring (lighter) */}
        <ellipse
          cx={e.cx}
          cy={e.cy + e.irisOy + 1}
          rx={e.irisRx * 0.7}
          ry={e.irisRy * 0.65}
          fill={irisColor}
          opacity={0.35}
          style={{ transition: t }}
        />

        {/* Pupil */}
        <circle
          cx={e.cx}
          cy={e.cy + e.pupilOy}
          r={e.pupilR}
          fill="#000"
          opacity={0.5}
          style={{ transition: t }}
        />

        {/* Heart overlay for love expression */}
        {e.heart && (
          <path
            d={`M ${e.cx},${e.cy + e.irisOy + 4}
                L ${e.cx - 5},${e.cy + e.irisOy - 2}
                Q ${e.cx - 5},${e.cy + e.irisOy - 7} ${e.cx},${e.cy + e.irisOy - 4}
                Q ${e.cx + 5},${e.cy + e.irisOy - 7} ${e.cx + 5},${e.cy + e.irisOy - 2} Z`}
            fill={irisColor}
            opacity={0.5}
            style={{ transition: t }}
          />
        )}

        {/* Primary sparkle — 4-pointed AI star */}
        <path
          d={sparklePath(
            e.cx + e.shine[0].dx,
            e.cy + e.shine[0].dy + e.irisOy * 0.3,
            Math.max(e.shine[0].rx, e.shine[0].ry) * 1.5
          )}
          fill="white"
          opacity={0.92}
          style={{ transition: tFast }}
        />

        {/* Secondary sparkle — smaller star */}
        <path
          d={sparklePath(
            e.cx + e.shine[1].dx,
            e.cy + e.shine[1].dy + e.irisOy * 0.3,
            e.shine[1].r * 2.2
          )}
          fill="white"
          opacity={0.75}
          style={{ transition: tFast }}
        />
      </g>
    </g>
  );
};

export default AnimeEye;
